import * as amplitude from '@amplitude/analytics-browser'
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser'
import isChromiumEdge from '~~/lib/is-chromium-edge'

/**
 * All analytics related code should be within this class which allows the
 * global usage of directives created (see OnClickAnalytics for an example) and methods
 * (used via this.$analytics.[method_name]) in other files.
 */

interface UserProperties {
  id: string | null
  name: string | null
  emailAddress: string | null
  profileImage: string | null
}

interface CompanyProperties {
  companyId: string | null
  companyName: string | null
}

/**
 * This class contains the methods necessary for analytics tracking
 */
export class Analytics {
  // private variable
  private static globalMetaData: { [key: string]: any } = {}
  private static userProperties: UserProperties = {
    id: null,
    name: null,
    emailAddress: null,
    profileImage: null,
  }
  private static companyProperties: CompanyProperties = {
    companyId: null,
    companyName: null,
  }

  /**
   * Sends a tracking events with some properties to the tracking services and returns the boolean association
   * of the response based on whether or not the calls were successful or not
   */
  static async track(eventName: string, analyticsEventMetaData: any = {}) {
    const isChromiumEdgeBrowser = isChromiumEdge()

    if (isChromiumEdgeBrowser) {
      analyticsEventMetaData.isChromiumBrowser = isChromiumEdgeBrowser
    }

    const amplitudeResponse = amplitude.logEvent(eventName, {
      ...this.companyProperties,
      ...this.globalMetaData,
      ...analyticsEventMetaData,
    })

    return !!amplitudeResponse
  }

  // changes the users tracking preferences to be opted out so that their actions are not tracked
  static async optOut() {
    amplitude.setOptOut(true)
  }

  // identifies a user under a unique user ID
  static identify(user: UserProperties) {
    amplitude.setUserId(String(user.id))
    const indentify = new amplitude.Identify()
    for (const [key, value] of Object.entries(user)) {
      indentify.set(key, value)
    }
    this.userProperties = user
  }

  static setCompany(companyProperties: CompanyProperties) {
    this.companyProperties = companyProperties

    if (this.userProperties.id && 'Vitally' in window) {
      const vitallyScript = window.Vitally as any
      vitallyScript.organization({
        organizationId: companyProperties.companyId,
        traits: {
          name: companyProperties.companyName,
        },
      })
      vitallyScript.user({
        userId: this.userProperties?.id,
        organizationId: companyProperties.companyId,
        traits: {
          name: this.userProperties?.name,
          email: this.userProperties?.emailAddress,
          avatar: this.userProperties?.profileImage,
        },
      })
      vitallyScript.nps('survey', {
        productName: 'VRIFY',
        delay: 5000,
        npsQuestion: ({ productName }: any) => `Hi there! Quick question - how likely are you to recommend ${productName} to others?`,
      })
    }
  }

  // unidentifies the user
  static unidentify() {
    this.globalMetaData = {}
    this.userProperties = {
      id: null,
      name: null,
      emailAddress: null,
      profileImage: null,
    }
    this.companyProperties = {
      companyId: null,
      companyName: null,
    }
    amplitude.reset()
    if ('Vitally' in window) {
      const vitallyScript = window.Vitally as any
      vitallyScript.organization(null)
      vitallyScript.user(null)
    }
  }

  static setGlobalMetaData(metaData: { [key: string]: any }, options: { override: boolean } = { override: false }) {
    this.globalMetaData = options.override ? metaData : { ...this.globalMetaData, ...metaData }
  }

  static resetGlobalMetaData() {
    this.globalMetaData = {}
  }
}

const handleAnalytics = (event: any) => {
  const eventName = event.currentTarget.event_name
  Analytics.track(eventName)
}

export default defineNuxtPlugin(nuxtApp => {
  const config = useRuntimeConfig()

  const token = config.public.AMPLITUDE_API_KEY as string
  if (!token) throw new Error('AMPLITUDE_API_KEY missing')

  amplitude.add(sessionReplayPlugin({ sampleRate: 1 }))
  amplitude.init(token, {
    transport: 'beacon',
    useBatch: true,
    minIdLength: 1,
    autocapture: {
      attribution: true,
      pageViews: true,
      sessions: true,
      formInteractions: true,
      fileDownloads: true,
      elementInteractions: true,
    },
  })

  nuxtApp.vueApp.directive('analytics', {
    mounted(el, binding) {
      el.event_name = binding.value
      el.addEventListener('click', handleAnalytics)
    },
    unmounted(el) {
      el.removeEventListener('click', handleAnalytics)
    },
  })

  return {
    provide: {
      analytics: Analytics,
    },
  }
})
